import React, { useMemo } from 'react';
import './CustomPanel.css';
import { ICollapsiblePanelProperties } from './CustomPanel.types'

import { CommandBarButton, IContextualMenuItem, mergeStyles, OverflowSet, useTheme } from '@fluentui/react';

import { ChevronRightIcon } from '@fluentui/react-icons-mdl2';
import { fadeIn } from 'global/Application';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { updateCollapse } from 'global/Reducer/statusSlice';

const visibleStyle = { opacity: '1', transition: 'opacity 1.4s', maxHeight: 'none' }
const invisibleStyle = { opacity: '0', transition: 'opacity 0s', maxHeight: '0', display: "none" }

export default function CustomPanel(props: ICollapsiblePanelProperties) {
    const { title, children, onCollapseChange, contentStyle, canCollapse, style, headerStyle } = props;
    const store = useSelector(state => state) as RootState;
    const defaultCollapsed = props.defaultCollapsed;
    const [isCollapsed, setIsCollapsed] = React.useState(
        !!props.storageId && store.status?.panelStatus[props.storageId] !== undefined
            ? store.status.panelStatus[props.storageId]
            : defaultCollapsed ?? false
    );

    const theme = useTheme();
    const dispatch = useDispatch();


    // make it controlled component if collapsed is supplied otherwise follow default/local state built on top of it.
    const collapsed = props.collapsed !== undefined ? props.collapsed : isCollapsed;


    React.useEffect(() => {
        // TODO We may need to fix this
        // issue : this component is either in controlled state, by passing in 'collapsed' 
        // or has a default init value provided by defaultCollapsed, and by providing storageId it starts to remember last state on mount 
        // once mounted we should be ignoring this
        // I think the solution for your case when the flip expand all, would be to force remount entire tree, it feels like its kind already doing so.

        if (!store.router.location.pathname.startsWith('/records/blueRelationship/'))
            setIsCollapsed(props.defaultCollapsed ?? false)

    }, [props.defaultCollapsed])

    // todo useMemo
    const collapsibleHeaderClass = useMemo(() => mergeStyles({
        backgroundColor: theme.semanticColors.menuBackground,
        color: theme.semanticColors.bodyText,
        // background:theme.palette.themePrimary,
        fontSize: theme.fonts.mediumPlus.fontSize,
        cursor: canCollapse ? 'pointer' : 'unset',
        display: 'flex',
        alignItems: 'center',
        minHeight: '45px',

        // borderBottom: '1px solid ' + theme.schemes?.soft?.semanticColors.menuDivider,
        ':hover': {
            backgroundColor: canCollapse ? theme.semanticColors.menuItemBackgroundHovered : 'unset'
        }
    }), [theme, canCollapse]);

    const headerOnClick = () => {
        if (!canCollapse)
            return;

        if (onCollapseChange)
            onCollapseChange(!collapsed);

        setIsCollapsed(!collapsed);

        if (props.storageId)
            dispatch(updateCollapse({ id: props.storageId, collapse: !collapsed }));
    }

    const items: IContextualMenuItem[] = props.items ?? [];

    return (
        <div key={props.id?.toLowerCase()} id={props.id?.toLowerCase()} style={{ maxWidth: '100%', marginBottom: theme.spacing.m, ...style, border: '2px solid rgba(180,180,180,0.1)' }}>
            {onRenderHeader()}
            <div className={fadeIn} style={
                Object.assign(
                    { padding: '0px 18px', overflow: 'hidden', transition: 'max-height 0.1s ease-out', maxHeight: '0', ...contentStyle },
                    collapsed ? invisibleStyle : visibleStyle
                )
            } >
                {(!props.mountWhenExpanded || (props.mountWhenExpanded && !collapsed)) &&
                    children
                }
            </div>
        </div>
    );

    function onRenderHeaderTitle() {
        return <div style={{ marginLeft: canCollapse ? '5px' : '15px', fontWeight: collapsed ? 300 : 450, ...headerStyle }}>{title}</div>
    }

    function onRenderHeaderContent() {
        return <>
            {(canCollapse) &&
                <div style={{ marginLeft: '15px', transition: '180ms ease all', transform: !collapsed ? 'rotate(90deg)' : '' }}>
                    <ChevronRightIcon style={{ fontSize: 'smaller' }} />
                </div>}

            {props.onRenderHeaderTitle
                ? props.onRenderHeaderTitle()
                : onRenderHeaderTitle()
            }
            <div style={{ marginLeft: 'auto', marginRight: '15px' }}>

                <OverflowSet
                    aria-label="Custom Example"
                    role="menubar"
                    items={items}
                    overflowItems={props.overflowItems}
                    onRenderItem={(item): JSX.Element => {
                        if (item.onRender) {
                            return item.onRender(item);
                        }

                        return (
                            <CommandBarButton
                                role="menuitem"
                                iconProps={{ iconName: item.icon }}
                                styles={{ root: { padding: '10px', margin: '0px' } }}
                                menuProps={item.subMenuProps}
                                text={item.name}
                                onClick={item.onClick} />
                        );
                    }}
                    onRenderOverflowButton={(renderProps, defaultRender) => {
                        return (
                            <CommandBarButton
                                ariaLabel="More items"
                                role="menuitem"
                                styles={{ root: { minWidth: 0, padding: '0px 4px', alignSelf: 'stretch', backgroundColor: 'transparent' }, }}
                                menuIconProps={{ iconName: 'More' }}
                                menuProps={{ items: renderProps! }} />
                        );
                    }} />
            </div>
        </>
    }

    function onRenderHeader() {
        return <div
            className={collapsibleHeaderClass}
            onClick={() => headerOnClick()}
            onKeyDown={(event) => {

                if (props.disabled)
                    return;

                // http://www.mit.edu/~rjc/aria/expandingSections.html#
                if (event.code === "Space" || event.code === "Enter" || event.code === "ArrowRight") {
                    event.preventDefault();
                    headerOnClick();
                }
            }}
            tabIndex={0}
        >
            {props.onRenderHeaderContent
                ? props.onRenderHeaderContent()
                : onRenderHeaderContent()}
        </div>;
    }
}
